import  { type Genre, type OnDemandItem, OnDemandData, ShowTypes } from "@ca.vmedia.tv/domains";
import { VodContentType } from "@ca.vmedia.tv/domains";
import { type CardData, OnDemandCardData } from "@/components/Cards/types";
import { Services } from "@ca.vmedia.tv/services";
import { useApplicationStore } from "@/stores/Application";
import { AppConstants } from "@/common";
import { useGenresStore } from "@/stores/Genres";
import { ShowAllContentType } from "@/components/Header/types/LinkItemsCollection";
import { Logger } from '@/utils';
import { ContentGenreType } from "@ca.vmedia.tv/swagger-api";
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { AxiosError } from 'axios';
import type { ExposeInterface } from './types';

export const useVodPageStore = defineStore('VodPage', (): ExposeInterface => {
    const genreUndefinedErrorMessage: string = 'Current genre is not defined';
    const { 
        emptyOnDemandData
    } = useEmptyLine();

    const { getGenre } = useGenresStore();

    const ApplicationStore = useApplicationStore();
    const { getFilterVodParams } = storeToRefs(ApplicationStore);
    const { orderBy } = useAppConfig();

    const _isLoadingShowAll: Ref<boolean> = ref(false);
    const getIsLoadingShowAll: ComputedRef<boolean> = computed(() => _isLoadingShowAll.value);
    const _setIsLoadingShowAll = (isLoading: boolean) => {
        _isLoadingShowAll.value = isLoading;
    };

    const _currentGenre: Ref<Genre | null> = ref(null);
    const getCurrentGenre = computed((): Genre | null => _currentGenre.value);
    const setCurrentGenre = (genre: Genre) => {
        _currentGenre.value = genre;
    };
    
    const genreType: ComputedRef<number> = computed((): number => {
        if (getCurrentGenre.value && getCurrentGenre.value.genreType){
            return getCurrentGenre.value.genreType;
        } else {
            return -1;
        }
    });

    const _showAllSubGenres: Ref<OnDemandData | null> = ref(null);
    const getShowAllSubGenresCards: ComputedRef<CardData[] | null> = computed(() => {
        if (_showAllSubGenres.value === null || _showAllSubGenres.value.items === null) {
            return null;
        }
        return _showAllSubGenres.value.items.map((item: OnDemandItem) => new OnDemandCardData(item));
    });
    const setShowAllSubGenres = (genreId: number): void => {
        if (_showAllSubGenres.value !== null) {
            return;
        }

        const genre = genreId ? getGenre(genreId) : _currentGenre.value;
        
        if (genre?.content) {
            const items = genre.content.items;
            const paging = genre.content.paging;
            _showAllSubGenres.value = new OnDemandData(items, null, paging);
        }
    };

    const loadShowAllSubGenresNextPage = async () => {
        if( _isLoadingShowAll.value === true){
            return;
        }

        const pageNumber = !_showAllSubGenres.value?.items || !_showAllSubGenres.value?.paging?.itemsOnPage
            ? -1
            : Math.ceil(_showAllSubGenres.value.items.length / _showAllSubGenres.value.paging.itemsOnPage) + 1;
        const totalPages = !_showAllSubGenres.value?.paging?.totalItems || !_showAllSubGenres.value?.paging?.itemsOnPage
            ? -1
            : Math.ceil(_showAllSubGenres.value.paging.totalItems / _showAllSubGenres.value.paging.itemsOnPage);

        if(pageNumber < 0 || totalPages < 0 || pageNumber > totalPages){
            return;
        }
        
        try {
            _setIsLoadingShowAll(true);

            let data = null;
            const { showType, cashDisabled } = getFilterVodParams.value;

            let currentGenre: Genre | null | undefined = _currentGenre.value;
            if (genreType.value == ContentGenreType.VodGenreType) {
                if (!currentGenre) {
                    Logger.captureError(new Error(genreUndefinedErrorMessage));
                    currentGenre = undefined;
                }
                data = await Services.Instance.vod.getVodContent(
                    VodContentType.GenreSubitems,
                    AppConstants.GenreItemsInLine,
                    pageNumber,
                    currentGenre,
                    showType
                );
            } else if (currentGenre !== null) {
                data = await Services.Instance.vod.getVodInternalContent(
                    VodContentType.GenreSubitems,
                    AppConstants.GenreItemsInLine,
                    pageNumber,
                    currentGenre,
                    142,
                    true,
                    showType,
                    orderBy.getVodInternalContent,
                    cashDisabled
                );
            } else {
                throw new Error(genreUndefinedErrorMessage);
            }
            
            if (data && data.items !== null && data.items.length != 0) {
                const items = (_showAllSubGenres.value?.items as OnDemandItem[]).concat(data.items);
                _showAllSubGenres.value = new OnDemandData(items, null, _showAllSubGenres.value?.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoadingShowAll(false);
        }
    };
    const fetchCurrentGenre = (genreId: number) => {
        const genre = getGenre(genreId);
        if (!genre) {
            throw new Error(genreUndefinedErrorMessage);
        }
        setCurrentGenre(genre);
    };

    const _newShows: Ref<OnDemandData | null> = ref(null);
    const getNewShows: ComputedRef<OnDemandData | null> = computed(() => _newShows.value);
    const fetchNewShows = async (): Promise<void> => {
        try {
            if (_newShows.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            if (showType === ShowTypes.Movie) {
                _newShows.value = new OnDemandData([], null, null);
                return;
            }

            _newShows.value = await Services.Instance.vod.getVodContent(
                VodContentType.NewSeries,
                AppConstants.ItemsInLine,
                1,
            );
        } catch (error) {
            _newShows.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };


    const _newMovies: Ref<OnDemandData | null> = ref(null);
    const getNewMovies: ComputedRef<OnDemandData | null> = computed(() => _newMovies.value);
    const fetchNewMovies = async (): Promise<void> => {
        try {
            if (_newMovies.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            if (showType === ShowTypes.Series) {
                _newMovies.value = new OnDemandData([], null, null);
                return;
            }

            _newMovies.value = await Services.Instance.vod.getVodContent(
                VodContentType.NewMovies,
                AppConstants.ItemsInLine,
                1,
            );
        } catch (error) {
            _newMovies.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _continueWatching: Ref<OnDemandData | null> = ref(null);
    const getContinueWatching: ComputedRef<OnDemandData | null> = computed(() => _continueWatching.value);
    const fetchContinueWatching = async (): Promise<void> => {
        try {
            if (_continueWatching.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            _continueWatching.value = await Services.Instance.vod.getVodContent(
                VodContentType.ContinueWatching,
                AppConstants.ItemsInLine,
                1,
                undefined,
                showType
            );
        } catch (error) {
            _continueWatching.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    
    const _recommendedMovies: Ref<OnDemandData | null> = ref(null);
    const getRecommendedMovie: ComputedRef<OnDemandData | null> = computed(() => _recommendedMovies.value);
    const fetchRecommendedMovies = async (): Promise<void> => {
        try {
            if (_recommendedMovies.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            if (showType === ShowTypes.Series) {
                _recommendedMovies.value = new OnDemandData([], null, null);
                return;
            }

            _recommendedMovies.value = await Services.Instance.vod.getVodContent(
                VodContentType.RecommendedMovies,
                AppConstants.ItemsInLine,
                1,
            );
        } catch (error) {
            _recommendedMovies.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _mostPopular: Ref<OnDemandData | null> = ref(null);
    const getMostPopular: ComputedRef<OnDemandData | null> = computed(() => _mostPopular.value);
    const fetchMostPopular= async (): Promise<void> => {
        try {
            if (_mostPopular.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            _mostPopular.value = await Services.Instance.vod.getVodContent(
                VodContentType.MostPopularVod,
                AppConstants.ItemsInLine,
                1,
                undefined,
                showType
            );
        } catch (error) {
            _mostPopular.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _newReleases: Ref<OnDemandData | null> = ref(null);
    const getNewReleases: ComputedRef<OnDemandData | null> = computed(() => _newReleases.value);
    const fetchNewReleases = async (): Promise<void> => {
        try {
            if (_newReleases.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            _newReleases.value = await Services.Instance.vod.getVodContent(
                VodContentType.RecentlyOnDemand,
                AppConstants.ItemsInLine,
                1,
                undefined,
                showType
            );
        } catch (error) {
            _newReleases.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };


    const _watchAgain: Ref<OnDemandData | null> = ref(null);
    const getWatchAgain: ComputedRef<OnDemandData | null> = computed(() => _watchAgain.value);
    const fetchWatchAgain = async (): Promise<void> => {
        try {
            if (_watchAgain.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            _watchAgain.value = await Services.Instance.vod.getVodContent(
                VodContentType.WatchAgain,
                AppConstants.ItemsInLine,
                1,
                undefined,
                showType
            );
        } catch (error) {
            _watchAgain.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _recommendedShows: Ref<OnDemandData | null> = ref(null);
    const getRecommendedShows: ComputedRef<OnDemandData | null> = computed(() => _recommendedShows.value);
    const fetchRecommendedShows = async (): Promise<void> => {
        try {
            if (_recommendedShows.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            if (showType === ShowTypes.Movie) {
                _recommendedShows.value = new OnDemandData([], null, null);
                return;
            }

            _recommendedShows.value = await Services.Instance.vod.getVodContent(
                VodContentType.RecommendedSeries,
                AppConstants.ItemsInLine,
                1,
            );
        } catch (error) {
            _recommendedShows.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    let _typePage: string | null = null;
    const _showAllItems: Ref<OnDemandData | null> = ref(null);
    const getShowAllCards: ComputedRef<CardData[] | null> = computed(() => {
        if (_showAllItems.value === null || _showAllItems.value.items === null) {
            return null;
        }
        
        return _showAllItems.value.items.map((item: OnDemandItem) => new OnDemandCardData(item));
    });
    const clearShowAllVod = (): void => {
        _showAllItems.value = null;
        _typePage = null;
        _showAllSubGenres.value = null;
    };
    const setShowAllVod = (type: string): void => {
        try {
            if (_showAllItems.value !== null) {
                return;
            };
            
            _typePage = type;
            
            switch (type) {
                case ShowAllContentType.NewSeries:
                    _showAllItems.value = new OnDemandData(_newShows.value?.items, null, _newShows.value?.paging);
                    break;

                case ShowAllContentType.NewMovies:
                    _showAllItems.value = new OnDemandData(_newMovies.value?.items, null, _newMovies.value?.paging);
                    break;

                case ShowAllContentType.ContinueWatching:
                    _showAllItems.value = new OnDemandData(_continueWatching.value?.items, null, _continueWatching.value?.paging);
                break;

                case ShowAllContentType.RecentlyOnDemand:
                    _showAllItems.value = new OnDemandData(_newReleases.value?.items, null, _newReleases.value?.paging);
                break;

                case ShowAllContentType.MostPopularVod:
                    _showAllItems.value = new OnDemandData(_mostPopular.value?.items, null, _mostPopular.value?.paging);
                break;

                case ShowAllContentType.WatchAgain:
                    _showAllItems.value = new OnDemandData(_watchAgain.value?.items, null, _watchAgain.value?.paging);
                break;

                case ShowAllContentType.RecommendedSeries:
                    _showAllItems.value = new OnDemandData(_recommendedShows.value?.items, null, _recommendedShows.value?.paging);
                break;

                case ShowAllContentType.RecommendedMovies:
                    _showAllItems.value = new OnDemandData(_recommendedMovies.value?.items, null, _recommendedMovies.value?.paging);
                break;
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const loadShowAllNextPage = async () => {
        if( _isLoadingShowAll.value === true){
            return;
        }
        const pageNumber = !_showAllItems.value?.items || !_showAllItems.value?.paging?.itemsOnPage
        ? -1
        : Math.ceil(_showAllItems.value.items.length / _showAllItems.value.paging.itemsOnPage) + 1;
        if(pageNumber < 0 || !_showAllItems.value?.paging?.totalPages || pageNumber > _showAllItems.value.paging?.totalPages){
            return;
        }

        try {
            _setIsLoadingShowAll(true);

            const { showType } = getFilterVodParams.value;
            const data = await Services.Instance.vod.getVodContent(
                VodContentType[_typePage as keyof typeof VodContentType],
                AppConstants.ItemsInLine,
                pageNumber,
                undefined,
                showType
            );
    
            if (data && data.items !== null && data.items.length != 0) {
                const items = (_showAllItems.value.items as OnDemandItem[]).concat(data.items);
                _showAllItems.value = new OnDemandData(items, null, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoadingShowAll(false);
        }
    };

    
    const clearVodPageByFilter = (): void => {
        _recommendedShows.value = null;
        _watchAgain.value = null;
        _newReleases.value = null;
        _mostPopular.value = null;
        _recommendedMovies.value = null;
        _continueWatching.value = null;
        _newMovies.value = null;
        _newShows.value = null;
    };
    const clearAllVodPage = (): void => {
        _currentGenre.value = null;
        _setIsLoadingShowAll(false);
        _showAllItems.value = null;
        _newMovies.value = null;
        _newShows.value = null;
        _watchAgain.value = null;
        _newReleases.value = null;
        _mostPopular.value = null;
        _continueWatching.value = null;
        _recommendedMovies.value = null;
        _recommendedShows.value = null;
        clearShowAllVod();
    };

    return {
        getCurrentGenre,
        getShowAllCards,
        getNewShows,
        getNewMovies,
        getRecommendedMovie,
        getMostPopular,
        getNewReleases,
        getWatchAgain,
        getRecommendedShows,
        getContinueWatching,
        getIsLoadingShowAll,
        genreType,
        getShowAllSubGenresCards,
        loadShowAllSubGenresNextPage,
        fetchCurrentGenre,
        loadShowAllNextPage,
        setCurrentGenre,
        clearAllVodPage,
        fetchNewShows,
        fetchNewMovies,
        fetchContinueWatching,
        fetchRecommendedMovies,
        fetchMostPopular,
        fetchNewReleases,
        fetchWatchAgain,
        fetchRecommendedShows,
        setShowAllVod,
        clearShowAllVod,
        setShowAllSubGenres,
        clearVodPageByFilter
    };
});